import type { GroupSpec, CycleRange } from '../../graph/domain/types';
import type { WorkflowGraph } from '../../graph';
import type { Store, GraphEditorState } from '../types';
import { generateId } from '../../graph/utils/id';
import { withSyncGuard } from '../sync/syncFlag';
import { pushUndo } from './historyActions';
import {
  validateCycleRange,
  validateNoOverlap,
  getGroupForOperation,
} from '../../graph/domain/groupValidation';
import {
  resolveCycleMembers,
  buildSnapshotFromStore,
} from '../../graph/domain/CycleMembershipResolver';
import {
  analyzeGroupSubgraph,
  validateSingleMembership,
} from '../../graph/domain/groupTopology';

/**
 * Custom error classes for group operations
 */
export class GroupValidationError extends Error {
  constructor(
    public code:
      | 'disconnected'
      | 'gap'
      | 'duplicate'
      | 'missing-step'
      | 'already-grouped',
    public details?: {
      missingStepIds?: string[];
      conflictingGroupId?: string;
    },
    message?: string,
  ) {
    super(message);
    this.name = 'GroupValidationError';
  }
}

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

/**
 * Helper to generate human-readable error message from topology analysis
 */
function getTopologyErrorMessage(
  analysis: { ok: false; reason: string; missingStepIds?: string[] },
  operationNodes?: Record<string, any>,
): string {
  /**
   * Convert step IDs to user-friendly step names
   */
  const formatStepId = (stepId: string): string => {
    if (!operationNodes?.[stepId]) return `Step (unknown)`;
    const step = operationNodes[stepId];
    return step.displayName || `Step ${stepId.slice(0, 8)}`;
  };

  switch (analysis.reason) {
    case 'disconnected':
      return 'Selected steps are not connected. Please select steps that form a single connected chain.';
    case 'gap':
      const missingSteps = (analysis.missingStepIds || []).map(formatStepId).join(', ');
      return `Selected steps have gaps. Missing: ${missingSteps || 'unknown'}.`;
    case 'duplicate':
      return 'Selected steps contain duplicates.';
    case 'missing-step':
      const missingStep = analysis.missingStepIds?.[0];
      return `Step not found: ${missingStep ? formatStepId(missingStep) : 'unknown'}.`;
    case 'already-grouped':
      return 'One or more steps already belong to another group.';
    default:
      return 'Invalid group selection.';
  }
}

function computeMemberCellIds(
  graphData: GraphEditorState['graphData'],
  group: GroupSpec,
  range: CycleRange,
): string[] {
  const startOpId = group.stepIds[range.startIndex];
  const endOpId = group.stepIds[range.endIndex];
  if (!startOpId || !endOpId) return [];
  const snapshot = buildSnapshotFromStore(graphData);
  const members = resolveCycleMembers(snapshot, {
    id: range.id,
    groupId: group.id!,
    startCellId: startOpId,
    endCellId: endOpId,
    repeatCount: range.iterationCount,
  });
  return [...members];
}

function recomputeAllMemberCellIds(
  graphData: GraphEditorState['graphData'],
  group: GroupSpec,
): CycleRange[] {
  return (group.cycleRanges ?? []).map((cr) => ({
    ...cr,
    memberCellIds: computeMemberCellIds(graphData, group, cr),
  }));
}

export function createGroupActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    addGroup(spec: GroupSpec): string {
      const state = get();
      
      // Validate group topology (connectivity, convexity, ordering)
      const analysis = analyzeGroupSubgraph(spec.stepIds, state.graphData);
      
      if (!analysis.ok) {
        throw new GroupValidationError(
          analysis.reason,
          {
            missingStepIds: analysis.missingStepIds,
            conflictingGroupId: analysis.conflictingGroupId,
          },
          getTopologyErrorMessage(analysis, state.graphData.operationNodes),
        );
      }

      // Validate single membership (not already in another group)
      for (const stepId of spec.stepIds) {
        const check = validateSingleMembership(stepId, state.graphData.groups);
        if (!check.valid) {
          throw new GroupValidationError(
            'already-grouped',
            { conflictingGroupId: check.conflictingGroupId },
            `Step is already in group "${check.conflictingGroupId}"`,
          );
        }
      }

      pushUndo(get, set, `Add group "${spec.label}"`);

      const id = spec.id ?? generateId('group');
      // Use normalized (topologically sorted) order from analysis
      const finalStepIds = analysis.ordered && analysis.ordered.length > 0 ? analysis.ordered : spec.stepIds;
      
      const fullSpec: GroupSpec = {
        ...spec,
        id,
        stepIds: finalStepIds,
        kind: 'group',
        cycleRanges: spec.cycleRanges ?? [],
        isCollapsed: spec.isCollapsed ?? false,
        createdAt: spec.createdAt ?? new Date().toISOString(),
      };

      set((s) => {
        const operationNodes = { ...s.graphData.operationNodes };
        for (let i = 0; i < fullSpec.stepIds.length; i++) {
          const sid = fullSpec.stepIds[i];
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], groupId: id, orderInGroup: i };
          }
        }
        return {
          graphData: {
            ...s.graphData,
            groups: { ...s.graphData.groups, [id]: fullSpec },
            operationNodes,
          },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.addGroup(fullSpec);
      });

      return id;
    },

    updateGroup(id: string, patch: Partial<GroupSpec>): void {
      const current = get().graphData.groups[id];
      if (!current) throw new Error(`Group not found: ${id}`);

      const updated = { ...current, ...patch };

      if (patch.stepIds) {
        // Validate updated group topology
        const analysis = analyzeGroupSubgraph(updated.stepIds, get().graphData);
        if (!analysis.ok) {
          throw new GroupValidationError(
            analysis.reason,
            {
              missingStepIds: analysis.missingStepIds,
              conflictingGroupId: analysis.conflictingGroupId,
            },
            getTopologyErrorMessage(analysis, get().graphData.operationNodes),
          );
        }

        // Validate single membership (excluding this group)
        for (const stepId of updated.stepIds) {
          if (!current.stepIds.includes(stepId)) {
            const check = validateSingleMembership(stepId, get().graphData.groups, id);
            if (!check.valid) {
              throw new GroupValidationError(
                'already-grouped',
                { conflictingGroupId: check.conflictingGroupId },
                `Step is already in group "${check.conflictingGroupId}"`,
              );
            }
          }
        }

        // Use normalized order
        updated.stepIds = analysis.ordered;

        // Remap cycle ranges by step ID, not index
        const oldIndexMap = new Map(current.stepIds.map((id, idx) => [id, idx]));
        const newIndexMap = new Map(analysis.ordered.map((id, idx) => [id, idx]));

        updated.cycleRanges = (current.cycleRanges ?? [])
          .map((cr) => {
            const startStepId = current.stepIds[cr.startIndex];
            const endStepId = current.stepIds[cr.endIndex];
            if (!startStepId || !endStepId) return null;

            const newStartIndex = newIndexMap.get(startStepId);
            const newEndIndex = newIndexMap.get(endStepId);
            if (newStartIndex === undefined || newEndIndex === undefined) return null;

            return { ...cr, startIndex: newStartIndex, endIndex: newEndIndex };
          })
          .filter((cr): cr is CycleRange => cr !== null);

        for (const cycle of updated.cycleRanges) {
          validateCycleRange(cycle, updated);
        }
        validateNoOverlap(updated.cycleRanges);
      }

      pushUndo(get, set, `Edit group "${updated.label}"`);

      set((s) => {
        const operationNodes = { ...s.graphData.operationNodes };
        // Clear old membership
        for (const sid of current.stepIds) {
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], groupId: undefined, orderInGroup: undefined };
          }
        }
        // Set new membership
        for (let i = 0; i < updated.stepIds.length; i++) {
          const sid = updated.stepIds[i];
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], groupId: id, orderInGroup: i };
          }
        }
        return {
          graphData: {
            ...s.graphData,
            groups: { ...s.graphData.groups, [id]: updated },
            operationNodes,
          },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.updateGroup(id, patch);
      });
    },

    removeGroup(id: string, options?: { cascadeSteps?: boolean }): void {
      const current = get().graphData.groups[id];
      if (!current) throw new Error(`Group not found: ${id}`);

      pushUndo(get, set, `Remove group "${current.label}"`);

      set((s) => {
        const { [id]: removed, ...restGroups } = s.graphData.groups;
        let operationNodes = { ...s.graphData.operationNodes };
        let flowEdges = { ...s.graphData.flowEdges };

        if (removed) {
          if (options?.cascadeSteps) {
            for (const sid of removed.stepIds) {
              delete operationNodes[sid];
              const newEdges: typeof flowEdges = {};
              for (const [eid, edge] of Object.entries(flowEdges)) {
                if (edge.sourceId !== sid && edge.targetId !== sid) {
                  newEdges[eid] = edge;
                }
              }
              flowEdges = newEdges;
            }
          } else {
            for (const sid of removed.stepIds) {
              if (operationNodes[sid]) {
                operationNodes[sid] = { ...operationNodes[sid], groupId: undefined, orderInGroup: undefined };
              }
            }
          }
        }

        return {
          graphData: { ...s.graphData, groups: restGroups, operationNodes, flowEdges },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.removeGroup(id, options);
      });
    },

    addStepsToGroup(groupId: string, stepIds: string[], insertAt?: number): void {
      const state = get();
      const group = state.graphData.groups[groupId];
      if (!group) throw new Error(`Group not found: ${groupId}`);

      // Validate single membership of new steps (excluding this group)
      for (const stepId of stepIds) {
        const check = validateSingleMembership(stepId, state.graphData.groups, groupId);
        if (!check.valid) {
          throw new GroupValidationError(
            'already-grouped',
            { conflictingGroupId: check.conflictingGroupId },
            `Step is already in group "${check.conflictingGroupId}"`,
          );
        }
      }

      // Validate union of existing + new steps
      const unionStepIds = [...new Set([...group.stepIds, ...stepIds])];
      const unionAnalysis = analyzeGroupSubgraph(unionStepIds, state.graphData);
      if (!unionAnalysis.ok) {
        throw new GroupValidationError(
          unionAnalysis.reason,
          {
            missingStepIds: unionAnalysis.missingStepIds,
          },
          getTopologyErrorMessage(unionAnalysis, state.graphData.operationNodes),
        );
      }

      pushUndo(get, set, `Add steps to "${group.label}"`);

      set((s) => {
        const g = s.graphData.groups[groupId];
        if (!g) return s;

        // Use the normalized union order (ignore insertAt, use topology)
        const current = unionAnalysis.ordered;

        // Adjust cycle ranges for any insertions
        const oldLength = g.stepIds.length;
        const newLength = current.length;
        const addedCount = newLength - oldLength;

        const adjustedCycles = (g.cycleRanges ?? [])
          .map((c) => {
            // If cycles span the insertion point, expand them
            // Otherwise, shift them if insertion happened before them
            const { startIndex, endIndex } = c;
            let newStart = startIndex;
            let newEnd = endIndex;

            // This is a simplification - in practice, we'd need to remap by step ID
            // For now, conservatively preserve the relative span
            if (addedCount > 0) {
              // Cycles will need to be recomputed via memberCellIds anyway
            }

            return { ...c, startIndex: newStart, endIndex: newEnd };
          });

        const operationNodes = { ...s.graphData.operationNodes };
        for (let i = 0; i < current.length; i++) {
          const sid = current[i];
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], groupId, orderInGroup: i };
          }
        }

        const updatedGroup: GroupSpec = {
          ...g,
          stepIds: current,
          cycleRanges: adjustedCycles,
        };

        const recomputedCycles = recomputeAllMemberCellIds(
          { ...s.graphData, operationNodes },
          updatedGroup,
        );

        return {
          graphData: {
            ...s.graphData,
            groups: {
              ...s.graphData.groups,
              [groupId]: { ...updatedGroup, cycleRanges: recomputedCycles },
            },
            operationNodes,
          },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.addStepsToGroup(groupId, stepIds);
      });
    },

    removeStepsFromGroup(groupId: string, stepIds: string[]): void {
      const group = get().graphData.groups[groupId];
      if (!group) throw new Error(`Group not found: ${groupId}`);

      const stepIdSet = new Set(stepIds);
      const remaining = group.stepIds.filter((sid) => !stepIdSet.has(sid));

      // Validate remainder stays connected (if not empty)
      if (remaining.length > 0) {
        const remainingAnalysis = analyzeGroupSubgraph(remaining, get().graphData);
        if (!remainingAnalysis.ok) {
          throw new GroupValidationError(
            remainingAnalysis.reason,
            { missingStepIds: remainingAnalysis.missingStepIds },
            `Cannot remove steps: remainder would become ${remainingAnalysis.reason}. ${getTopologyErrorMessage(remainingAnalysis, get().graphData.operationNodes)}`,
          );
        }
      }

      pushUndo(get, set, `Remove steps from "${group.label}"`);

      set((s) => {
        const g = s.graphData.groups[groupId];
        if (!g) return s;

        const remaining = g.stepIds.filter((sid) => !stepIdSet.has(sid));

        // Adjust cycle ranges for removals
        const adjustedCycles: CycleRange[] = [];
        for (const c of g.cycleRanges ?? []) {
          let { startIndex, endIndex } = c;
          // Count how many removed steps were before/within the cycle
          for (let i = g.stepIds.length - 1; i >= 0; i--) {
            if (!stepIdSet.has(g.stepIds[i])) continue;
            if (i < startIndex) {
              startIndex--;
              endIndex--;
            } else if (i <= endIndex) {
              endIndex--;
            }
          }
          if (endIndex >= startIndex) {
            adjustedCycles.push({ ...c, startIndex, endIndex });
          }
          // If endIndex < startIndex, the cycle is empty — drop it
        }

        const operationNodes = { ...s.graphData.operationNodes };
        for (const sid of stepIds) {
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], groupId: undefined, orderInGroup: undefined };
          }
        }
        for (let i = 0; i < remaining.length; i++) {
          const sid = remaining[i];
          if (operationNodes[sid]) {
            operationNodes[sid] = { ...operationNodes[sid], orderInGroup: i };
          }
        }

        // If group is now empty, remove it entirely
        let groups: typeof s.graphData.groups;
        if (remaining.length === 0) {
          const { [groupId]: _, ...rest } = s.graphData.groups;
          groups = rest;
        } else {
          const updatedGroup = { ...g, stepIds: remaining, cycleRanges: adjustedCycles };
          const recomputedCycles = recomputeAllMemberCellIds(
            { ...s.graphData, operationNodes },
            updatedGroup,
          );
          groups = {
            ...s.graphData.groups,
            [groupId]: { ...updatedGroup, cycleRanges: recomputedCycles },
          };
        }

        return {
          graphData: { ...s.graphData, groups, operationNodes },
          ui: { ...s.ui, isDirty: true },
        };
      });

      withSyncGuard(set, () => {
        getGraph()?.removeStepsFromGroup(groupId, stepIds);
      });
    },

    toggleGroupCollapsed(groupIds: string | string[]): void {
      const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    for (const groupId of ids) {
      const group = get().graphData.groups[groupId];
      if (!group) continue;
      get().setGroupsCollapsed([groupId], !group.isCollapsed);
    }
  },

  setGroupsCollapsed(groupIds: string[], collapsed: boolean): void {
    if (groupIds.length === 0) return;

    set((s) => {
      const updatedGroups = { ...s.graphData.groups };
      for (const id of groupIds) {
        if (updatedGroups[id]) {
          updatedGroups[id] = { ...updatedGroups[id], isCollapsed: collapsed };
        }
      }
      return { graphData: { ...s.graphData, groups: updatedGroups } };
    });

    withSyncGuard(set, () => {
      getGraph()?.foldGroup(groupIds, collapsed);
    });
  },

  updateGroupCollapsedState(groupId: string, isCollapsed: boolean): void {
      // Guard: if a programmatic fold is in progress via toggleGroupCollapsed,
      // syncInProgress is true and the store was already updated.
      // This prevents the cells-folded canvas event from double-writing.
      if (get().sync.syncInProgress) return;

      set((s) => ({
        graphData: {
          ...s.graphData,
          groups: {
            ...s.graphData.groups,
            [groupId]: { ...s.graphData.groups[groupId], isCollapsed },
          },
        },
      }));
      // No graph call — graph already performed the fold (user clicked icon)
    },

    // ── Cycle CRUD ─────────────────────────────────────────────────────────

    addCycle(groupId: string, range: Omit<CycleRange, 'id'>): string {
      const group = get().graphData.groups[groupId];
      if (!group) throw new Error(`Group not found: ${groupId}`);

      validateCycleRange(range, group);
      const allWithNew = [...(group.cycleRanges ?? []), { ...range, id: '__temp__' }];
      validateNoOverlap(allWithNew);

      pushUndo(get, set, `Add cycle to "${group.label}"`);

      const id = generateId('cycle');
      const fullRange: CycleRange = { id, ...range };
      
      set((s) => {
        const g = s.graphData.groups[groupId];
        if (!g) return s;
        fullRange.memberCellIds = computeMemberCellIds(s.graphData, g, fullRange);
        return {
          graphData: {
            ...s.graphData,
            groups: {
              ...s.graphData.groups,
              [groupId]: { ...g, cycleRanges: [...(g.cycleRanges ?? []), fullRange] },
            },
          },
          ui: { ...s.ui, isDirty: true },
        };
      });

      return id;
    },

    editCycle(groupId: string, cycleId: string, patch: Partial<Omit<CycleRange, 'id'>>): void {
      const group = get().graphData.groups[groupId];
      if (!group) throw new Error(`Group not found: ${groupId}`);

      const existing = (group.cycleRanges ?? []).find((c) => c.id === cycleId);
      if (!existing) throw new Error(`Cycle not found: ${cycleId}`);

      const updated = { ...existing, ...patch };
      validateCycleRange(updated, group);

      const newCycles = (group.cycleRanges ?? []).map((c) => (c.id === cycleId ? updated : c));
      validateNoOverlap(newCycles);

      pushUndo(get, set, `Edit cycle in "${group.label}"`);

      set((s) => {
        const g = s.graphData.groups[groupId];
        if (!g) return s;
        const recomputedCycles = newCycles.map((c) => ({
          ...c,
          memberCellIds: computeMemberCellIds(s.graphData, g, c),
        }));
        return {
          graphData: {
            ...s.graphData,
            groups: {
              ...s.graphData.groups,
              [groupId]: { ...g, cycleRanges: recomputedCycles },
            },
          },
          ui: { ...s.ui, isDirty: true },
        };
      });
    },

    removeCycle(groupId: string, cycleId: string): void {
      const group = get().graphData.groups[groupId];
      if (!group) throw new Error(`Group not found: ${groupId}`);

      pushUndo(get, set, `Remove cycle from "${group.label}"`);

      set((s) => {
        const g = s.graphData.groups[groupId];
        if (!g) return s;
        return {
          graphData: {
            ...s.graphData,
            groups: {
              ...s.graphData.groups,
              [groupId]: { ...g, cycleRanges: (g.cycleRanges ?? []).filter((c) => c.id !== cycleId) },
            },
          },
          ui: { ...s.ui, isDirty: true },
        };
      });
    },

    collapseAllGroups(): void {
      const groupIds = Object.keys(get().graphData.groups);
      get().setGroupsCollapsed(groupIds, true);
    },

    expandAllGroups(): void {
      const groupIds = Object.keys(get().graphData.groups);
      get().setGroupsCollapsed(groupIds, false);
    },
  };
}
